/* Mobile announcement rotation, scoped to each Shopify section instance. */
if (!customElements.get('tr-announcement-bar')) {
  customElements.define('tr-announcement-bar', class extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.items = Array.from(this.querySelectorAll('[data-announcement-id]'));
      if (!this.items.length) return;
      this.events = new AbortController();
      const listen = (target, name, handler, options = {}) => target.addEventListener(name, handler, { ...options, signal: this.events.signal });
      this.mobile = window.matchMedia('(max-width: 749px)');
      this.motion = window.matchMedia('(prefers-reduced-motion: reduce)');
      this.active = 0;
      this.paused = false;
      this.hovered = false;
      this.focused = false;
      this.editing = false;
      this.touchStart = null;
      this.inView = !('IntersectionObserver' in window);
      this.interval = Math.max(2000, Number(this.dataset.interval) || 5000);
      this.autoplay = this.dataset.autoRotateMobile === 'true';

      listen(this.mobile, 'change', () => this.render());
      listen(this.motion, 'change', () => this.schedule());
      listen(document, 'visibilitychange', () => this.schedule());
      listen(this, 'pointerenter', (event) => {
        if (event.pointerType !== 'touch') { this.hovered = true; this.schedule(); }
      });
      listen(this, 'pointerleave', () => { this.hovered = false; this.schedule(); });
      listen(this, 'focusin', () => { this.focused = true; this.schedule(); });
      listen(this, 'focusout', (event) => {
        if (!this.contains(event.relatedTarget)) { this.focused = false; this.schedule(); }
      });
      listen(this, 'keydown', (event) => {
        if (!this.mobile.matches || this.items.length < 2) return;
        if (event.key === 'ArrowRight' || event.key === 'ArrowLeft') {
          event.preventDefault();
          this.show(this.active + (event.key === 'ArrowRight' ? 1 : -1));
        } else if (event.key === ' ' || event.key === 'Escape') {
          event.preventDefault();
          this.paused = event.key === 'Escape' || !this.paused;
          this.schedule();
        }
      });
      listen(this, 'touchstart', (event) => {
        const touch = event.touches[0];
        this.touchStart = touch ? { x: touch.clientX, y: touch.clientY } : null;
        this.schedule();
      }, { passive: true });
      listen(this, 'touchend', (event) => {
        const start = this.touchStart;
        const touch = event.changedTouches[0];
        this.touchStart = null;
        if (this.mobile.matches && start && touch) {
          const dx = touch.clientX - start.x;
          const dy = touch.clientY - start.y;
          if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) this.show(this.active + (dx < 0 ? 1 : -1));
        }
        this.schedule();
      }, { passive: true });
      listen(this, 'touchcancel', () => { this.touchStart = null; this.schedule(); }, { passive: true });
      listen(this, 'shopify:block:select', (event) => {
        const index = this.items.findIndex(item => item.dataset.announcementId === event.detail?.blockId);
        if (index !== -1) { this.editing = true; this.show(index); }
      });
      listen(this, 'shopify:block:deselect', () => { this.editing = false; this.schedule(); });

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver((entries) => {
          this.inView = entries[0].isIntersecting;
          this.schedule();
        });
        this.observer.observe(this);
      }
      this.render();
    }

    show(index) {
      this.active = (index + this.items.length) % this.items.length;
      this.render();
    }

    render() {
      const mobile = this.mobile.matches;
      this.tabIndex = mobile && this.items.length > 1 ? 0 : -1;
      this.setAttribute('aria-label', mobile ? 'Store announcements. Swipe or use arrow keys for more messages. Space pauses or resumes rotation.' : 'Store announcements');
      this.items.forEach((item, index) => {
        const hidden = mobile && index !== this.active;
        item.classList.toggle('is-active', index === this.active);
        item.toggleAttribute('inert', hidden);
        if (hidden) item.setAttribute('aria-hidden', 'true');
        else item.removeAttribute('aria-hidden');
      });
      this.schedule();
    }

    schedule() {
      clearTimeout(this.timer);
      this.timer = null;
      if (!this.autoplay || !this.mobile.matches || this.items.length < 2 || this.motion.matches || this.paused || this.hovered || this.focused || this.editing || this.touchStart || !this.inView || document.hidden || !this.isConnected) return;
      this.timer = setTimeout(() => this.show(this.active + 1), this.interval);
    }

    disconnectedCallback() {
      clearTimeout(this.timer);
      this.events?.abort();
      this.observer?.disconnect();
      this.events = null;
    }
  });
}
